import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowUpIcon, CheckCheckIcon, CheckIcon, DownloadIcon, FileTextIcon, ImageIcon, PaperclipIcon, UploadIcon, XIcon } from 'lucide-react';
import type { CitizenRequest, RequestAttachment, RequestMessage } from '../../data/demandes';
import { ServiceSatisfaction, type ServiceRating } from './ServiceSatisfaction';
const ease = [0.23, 1, 0.32, 1] as const;
const READ_DELAY_MS = 3500;
const filledForm: RequestAttachment = {
  name: 'formulaire-reclamation-rempli.pdf',
  type: 'pdf',
  size: '212 Ko'
};
function AttachmentRow({
  file,
  tone
}: {
  file: RequestAttachment;
  tone: 'light' | 'dark';
}) {
  const Icon = file.type === 'image' ? ImageIcon : FileTextIcon;
  return <div className={`mt-3 flex items-center gap-3 rounded-2xl border px-3 py-2.5 ${tone === 'dark' ? 'border-white/20 bg-white/10' : 'border-slate-200 bg-white'}`} data-id="element-1393" data-mp="3ejfdg49bucbl">
      <span className={`grid h-9 w-9 shrink-0 place-items-center rounded-xl ${tone === 'dark' ? 'bg-white/15 text-white' : 'bg-ville-mist text-ville-deep'}`} aria-hidden="true" data-id="element-1394" data-mp="1k9li9d2mrvtp">
        <Icon className="h-4 w-4" data-id="element-1395" data-mp="1boft29qaalry" />
      </span>
      <span className="min-w-0 flex-1" data-id="element-1396" data-mp="1bp8rmnf3a4z0">
        <span className={`block truncate text-sm font-bold ${tone === 'dark' ? 'text-white' : 'text-ville-navy'}`} data-id="element-1397" data-mp="17n43g47eu0jr">
          {file.name}
        </span>
        <span className={`block text-xs ${tone === 'dark' ? 'text-white/60' : 'text-slate-500'}`} data-id="element-1398" data-mp="24cld0u9ik6qq">{file.size}</span>
      </span>
      <button type="button" aria-label={`Télécharger ${file.name}`} className={`grid h-9 w-9 shrink-0 place-items-center rounded-full transition-colors duration-150 ease-ville focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo ${tone === 'dark' ? 'text-white/70 hover:bg-white/10 hover:text-white' : 'text-slate-500 hover:bg-slate-100 hover:text-ville-navy'}`} data-id="element-1399" data-mp="1vcokghk06nqe">
        <DownloadIcon className="h-4 w-4" aria-hidden="true" data-id="element-1400" data-mp="ggnc636jyebv" />
      </button>
    </div>;
}
interface RequestMessagesProps {
  request: Pick<CitizenRequest, 'messages'> & {
    id?: string;
  };
  /** Évaluation du service, partagée avec l'en-tête de la fiche. */
  rating?: ServiceRating | null;
  onRate?: (rating: ServiceRating) => void;
}

/** Le fil de messages sert autant aux requêtes qu'aux demandes de permis. */
export function RequestMessages({
  request,
  rating = null,
  onRate
}: RequestMessagesProps) {
  const [messages, setMessages] = useState<RequestMessage[]>(request.messages);
  const [draft, setDraft] = useState('');
  const [pendingFile, setPendingFile] = useState<RequestAttachment | null>(null);
  const areaRef = useRef<HTMLTextAreaElement | null>(null);
  const idRef = useRef(0);
  const timersRef = useRef<number[]>([]);
  useEffect(() => {
    setMessages(request.messages);
    setDraft('');
    setPendingFile(null);
  }, [request.id, request.messages]);
  useEffect(() => {
    const el = areaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
  }, [draft]);
  const awaiting = [...messages].reverse().find(message => message.author === 'ville' && message.awaiting);
  const send = () => {
    const body = draft.trim();
    if (!body && !pendingFile) return;
    idRef.current += 1;
    setMessages(current => [...current, {
      id: `local-${idRef.current}`,
      author: 'citoyen',
      authorName: 'Vous',
      sentAt: 'À l’instant',
      body: body || 'Voici le document demandé.',
      attachment: pendingFile ?? undefined
    }]);
    setDraft('');
    setPendingFile(null);

    // Simule la lecture par le service quelques secondes plus tard.
    const id = `local-${idRef.current}`;
    const timer = window.setTimeout(() => {
      setMessages(current => current.map(message => message.id === id ? {
        ...message,
        readAt: 'À l’instant'
      } : message));
    }, READ_DELAY_MS);
    timersRef.current.push(timer);
  };
  useEffect(() => {
    const timers = timersRef.current;
    return () => timers.forEach(timer => window.clearTimeout(timer));
  }, []);
  return <section aria-labelledby="messages-titre" className="rounded-3xl border border-slate-200" data-id="element-1401" data-mp="21150ri4k2o3k">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-6 py-4" data-id="element-1402" data-mp="2505isyf7lmpi">
        <h2 id="messages-titre" className="text-lg font-extrabold tracking-tight text-ville-navy" data-id="element-1403" data-mp="18cr5390xw3v6">
          Messages
        </h2>
        <span className="rounded-full bg-ville-mist px-3 py-1 text-xs font-bold text-ville-deep" data-id="element-1404" data-mp="1dc3zceu54d7s">
          Réponse en 2 jours ouvrables
        </span>
      </div>

      {awaiting && <div className="border-b border-slate-100 bg-ville-sand px-6 py-4" data-id="element-1405" data-mp="1m8uver8ozs2d">
          <p className="text-sm font-extrabold text-ville-navy" data-id="element-1406" data-mp="2sqhy4ucsalq4">
            {awaiting.awaiting === 'document' ? 'La Ville attend un document de votre part' : 'La Ville attend une précision de votre part'}
          </p>
          <p className="mt-1 text-sm text-slate-600" data-id="element-1407" data-mp="32v84rdj4ue0o">
            {awaiting.awaiting === 'document' ? 'Remplissez le formulaire joint au dernier message, puis retournez-le ici.' : 'Répondez au dernier message pour que l’équipe puisse poursuivre le traitement.'}
          </p>
          {awaiting.awaiting === 'document' && <button type="button" onClick={() => setPendingFile(filledForm)} className="mt-3 inline-flex items-center gap-2 rounded-full bg-ville-navy px-5 py-2.5 text-sm font-bold text-white transition-transform duration-150 ease-ville hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo focus-visible:ring-offset-2" data-id="element-1408" data-mp="2p54nz230vcim">
              <UploadIcon className="h-4 w-4" aria-hidden="true" data-id="element-1409" data-mp="dbwm7xu8tyob" />
              Joindre le formulaire rempli
            </button>}
        </div>}

      <ol role="log" aria-live="polite" className="space-y-4 px-6 py-6" data-id="element-1410" data-mp="22u38zokixe51">
        {messages.map(message => message.author === 'citoyen' ? <motion.li key={message.id} initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.2,
        ease
      }} className="flex flex-col items-end" data-id="element-1411" data-mp="1tugb3cr06qgq">
              <div className="max-w-[85%] rounded-3xl rounded-br-lg bg-ville-navy px-5 py-3.5 text-[15px] leading-relaxed text-white" data-id="element-1412" data-mp="2lm5jsjahi07d">
                {message.body}
                {message.attachment && <AttachmentRow file={message.attachment} tone="dark" data-id="element-1413" data-mp="2ck653xmxz87l" />}
              </div>
              <p className="mt-1.5 flex items-center gap-1.5 pr-1 text-xs text-slate-400" data-id="element-1414" data-mp="2hipphtlw0bxu">
                {message.sentAt}
                <span aria-hidden="true" data-id="element-1415" data-mp="114hfeyiazv33">·</span>
                {message.readAt ? <span className="inline-flex items-center gap-1 font-semibold text-ville-deep" data-id="element-1416" data-mp="166gi0667ynyz">
                    <CheckCheckIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-1417" data-mp="xma7ztv3mmjp" />
                    {message.readAt === 'À l’instant' ? 'Lu' : `Lu le ${message.readAt}`}
                  </span> : <span className="inline-flex items-center gap-1" data-id="element-1418" data-mp="17jydneu8tegn">
                    <CheckIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-1419" data-mp="12o9c55ynxnhp" />
                    Envoyé
                  </span>}
              </p>
            </motion.li> : <motion.li key={message.id} initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.2,
        ease
      }} className="flex flex-col items-start" data-id="element-1420" data-mp="9zljgpbl3vu4">
              <div className="max-w-[88%] rounded-3xl rounded-bl-lg border border-slate-200 bg-white px-5 py-3.5" data-id="element-1421" data-mp="3sc083yd8eyj8">
                <p className="text-xs font-extrabold uppercase tracking-wider text-ville-deep" data-id="element-1422" data-mp="3ndypfejpx18a">
                  {/* Toujours signé par la Ville : ni service, ni nom d'employé. */}
                  Ville de Brossard
                </p>
                <p className="mt-1.5 text-[15px] leading-relaxed text-ville-navy" data-id="element-1423" data-mp="36t7467pmr5my">{message.body}</p>
                {message.attachment && <AttachmentRow file={message.attachment} tone="light" data-id="element-1424" data-mp="3bueas3g1dys1" />}
                {message.awaiting === 'document' && <p className="mt-2 text-xs font-semibold text-ville-indigo" data-id="element-1425" data-mp="3kt9ijjyqbgxg">Document à remplir et à retourner</p>}
                {message.awaiting === 'info' && <p className="mt-2 text-xs font-semibold text-ville-indigo" data-id="element-1426" data-mp="3uit10oij58rs">Réponse attendue</p>}
                {message.satisfaction && onRate && <ServiceSatisfaction value={rating} onChange={onRate} variant="message" data-id="element-1427" data-mp="3eb2bvh3o62n" />}
              </div>
              <p className="mt-1.5 pl-1 text-xs text-slate-400" data-id="element-1428" data-mp="ccn32qbokk8t">{message.sentAt}</p>
            </motion.li>)}
      </ol>

      <div className="border-t border-slate-100 px-4 pb-4 pt-3 sm:px-6" data-id="element-1429" data-mp="1wvqj8s6c1lfg">
        <form onSubmit={event => {
        event.preventDefault();
        send();
      }} className="rounded-[24px] border border-slate-200 px-4 pb-3 pt-3.5 transition-colors duration-150 ease-ville focus-within:border-ville-indigo" data-id="element-1430" data-mp="1ssu6hi3h4jjf">
          <label htmlFor="message-ville" className="block text-xs font-bold text-slate-500" data-id="element-1431" data-mp="2giy9bn8u8f9d">
            Écrire un message à la Ville de Brossard
          </label>
          <textarea id="message-ville" ref={areaRef} rows={1} value={draft} onChange={event => setDraft(event.target.value)} onKeyDown={event => {
          if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            send();
          }
        }} className="block max-h-40 w-full resize-none border-0 bg-transparent px-1 text-base leading-relaxed text-ville-navy placeholder:text-slate-400 focus:outline-none" data-id="element-1432" data-mp="2bkd6rh645jhr" />

          {pendingFile && <div className="mt-2 flex items-center gap-2 rounded-full bg-ville-indigo-soft py-1.5 pl-3 pr-1.5 text-xs font-bold text-ville-indigo" data-id="element-1433" data-mp="wjoi9oigml70">
              <FileTextIcon className="h-4 w-4" aria-hidden="true" data-id="element-1434" data-mp="16gkrxxk3hdd5" />
              <span className="truncate" data-id="element-1435" data-mp="16ie23mtx2fpw">{pendingFile.name}</span>
              <button type="button" onClick={() => setPendingFile(null)} aria-label="Retirer le fichier joint" className="grid h-6 w-6 shrink-0 place-items-center rounded-full hover:bg-white/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-1436" data-mp="1keristqmmd3b">
                <XIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-1437" data-mp="236k0vt45qdw9" />
              </button>
            </div>}

          <div className="mt-1.5 flex items-center justify-between gap-3" data-id="element-1438" data-mp="1z5ia80ob3a5w">
            <button type="button" onClick={() => setPendingFile(filledForm)} aria-label="Joindre un fichier" className="grid h-10 w-10 place-items-center rounded-full text-slate-500 transition-colors duration-150 ease-ville hover:bg-slate-100 hover:text-ville-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-1439" data-mp="1z35m15ryg3ke">
              <PaperclipIcon className="h-5 w-5" aria-hidden="true" data-id="element-1440" data-mp="1z2rhei3dp9uh" />
            </button>
            <button type="submit" disabled={draft.trim().length === 0 && !pendingFile} aria-label="Envoyer le message" className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-ville-lime text-ville-navy transition-[transform,opacity] duration-150 ease-ville hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400 disabled:hover:translate-y-0" data-id="element-1441" data-mp="23thoq0vd7mrt">
              <ArrowUpIcon className="h-5 w-5" aria-hidden="true" data-id="element-1442" data-mp="3mtgxoudshf1i" />
            </button>
          </div>
        </form>
      </div>
    </section>;
}